import React, { useEffect, useMemo, useState } from 'react';
import { CheckSquare, Plus, Search, Users, CheckCircle, Circle, AlertCircle, Calendar, CalendarDays, Trash2, Edit, CalendarX2 } from 'lucide-react';
import { parseISO, isToday, isPast, isTomorrow } from 'date-fns';
import { faelligkeitAnzeige } from '../../../lib/faelligkeit';
import { Button } from '../../../components/ui/button';
import LoeschenMitGrundDialog from '../../../components/ui/loeschen-mit-grund-dialog';
import { Input } from '../../../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../../../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '../../../components/auth/use-auth';
import { usePermissions } from '../../../components/rbac/use-permission';
import { P } from '../../../lib/rbac/permissions.generated';
import { tasksApi, type Task } from '../../../lib/tasks';
import TaskForm, { type TaskFormValues } from '../../../components/tasks/TaskForm';
import { useStandort } from '../../../components/layouts/use-standort';
import { api } from '../../../lib/api';
import CourseCancelWizardModal, { type CourseCancelSeed } from '../../../components/schedule/CourseCancelWizardModal';
import CancelTrialWizardModal from '../../../components/trials/CancelTrialWizardModal';
import { fetchTrial } from '../../../lib/trials';
import type { TrialLessonDTO } from '../../../lib/trials';

type TabKey = 'all' | 'today' | 'upcoming' | 'overdue' | 'completed';

interface BackendUser { id: number; vorname: string; nachname: string; email: string; rolle: string; }

const TodoList = () => {
  const { user } = useAuth();
  const { selectedStandortId } = useStandort();
  const { can } = usePermissions();
  // Aufgaben anderer Benutzer: wer über das eigene Team hinaus sieht und die
  // Benutzerliste laden darf — laut Server, nicht nach Rolle.
  const canManageOthers =
    can(P.arbeit.aufgaben.aufgaben_ansehen, { minScope: 'location' })
    && can(P.standort.benutzer_und_zugriffsrechte.standortbenutzer_ansehen);
  const currentUserId = useMemo(() => (user ? parseInt(user.id, 10) : undefined), [user]);

  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabKey>('today');
  const [search, setSearch] = useState('');
  const [assigneeId, setAssigneeId] = useState<number | 'all'>(currentUserId || 'all');
  const [users, setUsers] = useState<Array<{ id: number; name: string }>>([]);

  const [selected, setSelected] = useState<Task | null>(null);
  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  // T-0190: gemeldete Abwesenheit über den vorhandenen Absage-Weg absagen.
  const [kursAbsage, setKursAbsage] = useState<CourseCancelSeed | null>(null);
  const [probestundeAbsage, setProbestundeAbsage] = useState<TrialLessonDTO | null>(null);

  const abwesenheitAbsagen = async (t: Task) => {
    const a = t.abwesenheit;
    if (!a) return;
    try {
      if (a.trialId) {
        const trial = await fetchTrial(a.trialId);
        if (!trial) throw new Error('Probestunde nicht gefunden');
        setProbestundeAbsage(trial);
        return;
      }
      if (!a.kursId) return;
      const teilnehmer = await api.get<CourseCancelSeed['participants']>(`/api/kurse/${a.kursId}/participants`);
      const tag = new Date(`${a.datum}T00:00:00`);
      setKursAbsage({
        id: `abwesenheit-${a.id}`,
        courseId: a.kursId,
        name: a.kursName || 'Kurs',
        studentName: (teilnehmer || []).map((p) => p.name).join(', '),
        studentEmail: '',
        teacherId: String(a.lehrerId),
        roomId: a.raumId,
        dayOfWeek: ((tag.getDay() + 6) % 7) + 1,
        startTime: (a.beginn || '').slice(0, 5),
        endTime: (a.ende || '').slice(0, 5),
        participants: teilnehmer || [],
        cancelDate: a.datum,
        cancelledBy: 'schule',
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Termin konnte nicht geladen werden');
    }
  };

  const loadUsers = async () => {
    if (!canManageOthers) {
      if (currentUserId) setUsers([{ id: currentUserId, name: user?.name || `User #${currentUserId}` }]);
      return;
    }
    try {
      const all = await (await import('../../../lib/api')).api.get<BackendUser[]>('/api/users');
      setUsers(all.map(u => ({ id: u.id, name: `${u.vorname} ${u.nachname}`.trim() })));
    } catch (_) {
      setUsers([]);
    }
  };

  const loadTasks = async () => {
    if (!selectedStandortId) return;
    setLoading(true);
    try {
      const params: any = {};
      if (assigneeId !== 'all') params.assignedUserId = assigneeId;
      // Für Tabs laden wir alle und filtern client-seitig (Backend kennt nur open|all)
      params.status = 'all';
      params.standortId = selectedStandortId;
      params.inklUnzugewiesen = true;
      const list = await tasksApi.list(params);
      setTasks(list);
    } catch (e) {
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadUsers(); }, [canManageOthers, currentUserId]);
  useEffect(() => { loadTasks(); }, [assigneeId, selectedStandortId]);

  const filtered = useMemo(() => {
    const matchesTab = (t: Task): boolean => {
      const due = parseISO(t.dueAt.replace(' ', 'T'));
      switch (activeTab) {
        case 'today': return !t.isDone && (isToday(due) || (isPast(due) && !isToday(due)));
        case 'upcoming': return !isPast(due) && !isToday(due) && !t.isDone;
        case 'overdue': return isPast(due) && !isToday(due) && !t.isDone;
        case 'completed': return t.isDone === true;
        case 'all':
        default: return true;
      }
    };
    return tasks
      .filter(t => matchesTab(t))
      .filter(t => search.trim() === '' || t.title.toLowerCase().includes(search.toLowerCase()) || (t.description || '').toLowerCase().includes(search.toLowerCase()))
      .sort((a, b) => parseISO(a.dueAt.replace(' ', 'T')).getTime() - parseISO(b.dueAt.replace(' ', 'T')).getTime());
  }, [tasks, activeTab, search]);

  const handleCreate = async (values: TaskFormValues) => {
    try {
      await tasksApi.create(values);
      toast.success('Aufgabe erstellt');
      setOpenCreate(false);
      await loadTasks();
    } catch (e) {
      toast.error('Aufgabe konnte nicht erstellt werden');
    }
  };
  const handleEdit = async (values: TaskFormValues) => {
    if (!selected) return;
    try {
      await tasksApi.update(selected.id, values);
      toast.success('Aufgabe aktualisiert');
      setOpenEdit(false);
      await loadTasks();
    } catch (e) {
      toast.error('Aufgabe konnte nicht aktualisiert werden');
    }
  };
  const handleToggle = async (t: Task) => {
    try {
      await tasksApi.toggleDone(t.id, !t.isDone);
      await loadTasks();
    } catch (e) {
      toast.error('Status konnte nicht geändert werden');
    }
  };
  const handleDelete = async (grund: string) => {
    if (!selected) return;
    await tasksApi.remove(selected.id, grund);
    toast.success('Aufgabe gelöscht');
    setOpenDelete(false);
    await loadTasks();
  };

  return <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold leading-7">Aufgabenliste</h1>
          <p className="text-muted-foreground">
          {canManageOthers ? 'Aufgaben aller Benutzer verwalten' : 'Deine zugewiesenen Aufgaben'}
          </p>
        </div>
      <Button onClick={() => setOpenCreate(true)}>
        <Plus className="mr-2 h-4 w-4" /> Neue Aufgabe
        </Button>
      </div>

      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
      <Tabs value={activeTab} onValueChange={v => setActiveTab(v as TabKey)} className="w-full md:w-auto">
          <TabsList>
          <TabsTrigger value="today" className="flex items-center gap-1"><Calendar className="h-4 w-4" />Heute</TabsTrigger>
          <TabsTrigger value="upcoming" className="flex items-center gap-1"><CalendarDays className="h-4 w-4" />Anstehend</TabsTrigger>
          <TabsTrigger value="overdue" className="flex items-center gap-1"><AlertCircle className="h-4 w-4" />Überfällig</TabsTrigger>
          <TabsTrigger value="completed" className="flex items-center gap-1"><CheckCircle className="h-4 w-4" />Erledigt</TabsTrigger>
          <TabsTrigger value="all" className="flex items-center gap-1"><CheckSquare className="h-4 w-4" />Alle</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Suchen..." className="pl-8 md:w-[220px]" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        {(
          <Select value={assigneeId === 'all' ? 'all' : String(assigneeId)} onValueChange={v => setAssigneeId(v === 'all' ? 'all' : parseInt(v, 10))}>
            <SelectTrigger className="w-[200px]"><div className="flex items-center"><Users className="mr-2 h-4 w-4" /><span>Benutzer</span></div></SelectTrigger>
            <SelectContent>
              {canManageOthers && <SelectItem value="all">Alle Benutzer</SelectItem>}
              {users.map(u => <SelectItem key={u.id} value={String(u.id)}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        </div>
      </div>

    {loading ? (
      <div className="flex h-64 items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" /></div>
    ) : filtered.length === 0 ? (
      <Card className="text-center p-8">
          <CheckSquare className="mx-auto h-12 w-12 text-muted-foreground" />
        <h2 className="mt-4 text-lg font-semibold">Keine Aufgaben gefunden</h2>
        <p className="mt-2 text-sm text-muted-foreground">Passe die Filter an oder erstelle eine neue Aufgabe.</p>
        <Button className="mt-4" onClick={() => setOpenCreate(true)}><Plus className="mr-2 h-4 w-4" />Neue Aufgabe erstellen</Button>
      </Card>
    ) : (
      <div className="space-y-2">
        {filtered.map(t => {
          const due = parseISO(t.dueAt.replace(' ', 'T'));
          const isOverdue = !t.isDone && isPast(due) && !isToday(due);
          const isDueToday = !t.isDone && isToday(due);
          return (
            <Card key={t.id}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <button className="mt-0.5" onClick={() => handleToggle(t)} aria-label={t.isDone ? 'Als offen markieren' : 'Als erledigt markieren'}>
                      {t.isDone ? <CheckCircle className="h-5 w-5 text-green-500" /> : <Circle className="h-5 w-5 text-blue-500" />}
                    </button>
                    <div>
                      <div className={`font-medium ${t.isDone ? 'line-through text-muted-foreground' : ''}`}>{t.title}</div>
                      {t.description && <div className="mt-0.5 text-sm text-muted-foreground whitespace-pre-wrap">{t.description}</div>}
                      <div className="mt-1 text-xs text-muted-foreground">
                        Fällig: <span className={`${isOverdue ? 'text-red-600 font-medium' : isDueToday ? 'text-amber-600 font-medium' : ''}`}>{faelligkeitAnzeige(t.dueAt)}</span>
                      </div>
                      {t.abwesenheit && t.abwesenheit.status === 'gemeldet' && !t.isDone ? (
                        <Button size="sm" variant="outline" className="mt-2" onClick={() => void abwesenheitAbsagen(t)}>
                          <CalendarX2 className="mr-2 h-4 w-4" /> Absagen und Schüler informieren
                        </Button>
                      ) : null}
                      </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0"><span className="sr-only">Menü öffnen</span><Edit className="h-4 w-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => { setSelected(t); setOpenEdit(true); }}>Bearbeiten</DropdownMenuItem>
                          <DropdownMenuSeparator />
                      <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => { setSelected(t); setOpenDelete(true); }}>
                        <Trash2 className="mr-2 h-4 w-4" /> Löschen
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                </div>
          </CardContent>
            </Card>
          );
        })}
      </div>
    )}

    {kursAbsage ? (
      <CourseCancelWizardModal
        open={kursAbsage !== null}
        onOpenChange={(offen) => { if (!offen) setKursAbsage(null); }}
        course={kursAbsage}
        onDone={() => { setKursAbsage(null); void loadTasks(); }}
      />
    ) : null}
    {probestundeAbsage ? (
      <CancelTrialWizardModal
        open={probestundeAbsage !== null}
        onOpenChange={(offen) => { if (!offen) setProbestundeAbsage(null); }}
        trial={probestundeAbsage}
        onDone={() => { setProbestundeAbsage(null); void loadTasks(); }}
      />
    ) : null}

    {/* Create dialog */}
    <Dialog open={openCreate} onOpenChange={setOpenCreate}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Neue Aufgabe</DialogTitle>
          {/* Nur für Screenreader — sichtbar wirkte es wie ein Untertitel (T-0256). */}
          <DialogDescription className="sr-only">Pflicht: Titel, Fällig am und Zugewiesen an</DialogDescription>
        </DialogHeader>
        <TaskForm
          initial={{ assignedUserId: assigneeId === 'all' ? (currentUserId || 0) : assigneeId, standortId: selectedStandortId || undefined }}
          onSubmit={handleCreate}
          onCancel={() => setOpenCreate(false)}
          users={users.length ? users : (currentUserId ? [{ id: currentUserId, name: user?.name || `User #${currentUserId}` }] : [])}
        />
      </DialogContent>
    </Dialog>

    {/* Edit dialog */}
    <Dialog open={openEdit} onOpenChange={setOpenEdit}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Aufgabe bearbeiten</DialogTitle>
        </DialogHeader>
        {selected && (
          <TaskForm
            initial={{ title: selected.title, description: selected.description || '', dueAt: selected.dueAt, assignedUserId: selected.assignedUserId, personHistoryId: selected.personHistoryId || undefined, standortId: selected.standortId || undefined }}
            onSubmit={handleEdit}
            onCancel={() => setOpenEdit(false)}
            users={users.length ? users : (currentUserId ? [{ id: currentUserId, name: user?.name || `User #${currentUserId}` }] : [])}
          />
        )}
      </DialogContent>
    </Dialog>

    {/* Delete dialog */}
    <LoeschenMitGrundDialog
      offen={openDelete}
      titel={`Aufgabe „${selected?.title ?? ''}“ löschen?`}
      onAbbrechen={() => setOpenDelete(false)}
      onLoeschen={handleDelete}
    />
    </div>;
};

export default TodoList;